import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { UpgradeModal } from "@/modals/UpgradeModal";
import { BrBox } from "@/components/BrBox";
import { BrBtn } from "@/components/BrBtn";
import { BrTag } from "@/components/BrTag";
import { Logo } from "@/components/Logo";
import { ChessBoard } from "@/chess/board/ChessBoard";
import { fenToBoard, offsetShadow, TYPE } from "@chess/shared";
import { useTheme } from "@/theme/ThemeProvider";
import { useViewport } from "@/util/viewport";
import { PricingSection } from "./PricingSection";

const PREVIEW_FEN = "r1bq1rk1/ppp2ppp/2np1n2/4p3/2B1P3/5N2/PPPP1PPP/R1BQ1RK1 w - - 0 1";

export function LandingPage() {
  const p = useTheme();
  const { isMobile } = useViewport();
  const nav = useNavigate();
  const auth = useAuth();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const onStart = () => nav("/login");
  // Signed-in visitors hitting the Pro CTA go straight to checkout, not signup.
  const onStartPro = auth.signedIn ? () => setUpgradeOpen(true) : undefined;
  const board = fenToBoard(PREVIEW_FEN);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: p.bg,
        color: p.fg,
        fontFamily: TYPE.body,
        transition: "background .25s, color .25s",
      }}
    >
      <nav
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          height: isMobile ? 60 : 72,
          padding: isMobile ? "0 16px" : "0 40px",
          background: p.bg,
          borderBottom: `2px solid ${p.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          transition: "background .25s, border-color .25s",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 12 : 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: isMobile ? 18 : 22, fontWeight: 800, letterSpacing: -0.5 }}>
            <Logo size={isMobile ? 30 : 36} />
            GAMBIT.
          </div>
          {!isMobile && (
            <div style={{ display: "flex", gap: 24, fontSize: 12.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
              <span style={{ cursor: "pointer" }}>Product</span>
              <span style={{ cursor: "pointer" }}>Training</span>
              <span style={{ cursor: "pointer" }} onClick={() => { const el = document.getElementById("pricing"); if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: "smooth" }); }}>Pricing</span>
              <span style={{ cursor: "pointer" }}>Manifesto</span>
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {!isMobile && <BrBtn variant="ghost" size="sm" withShadow={false} onClick={onStart}>Log in</BrBtn>}
          <BrBtn variant="accent" size="sm" onClick={onStart}>Start →</BrBtn>
        </div>
      </nav>

      <section
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          padding: isMobile ? "28px 16px 36px" : "60px 40px 76px",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1.15fr 1fr",
          gap: isMobile ? 28 : 56,
          alignItems: "start",
        }}
      >
        <div>
          <BrTag bg={p.danger} fg={p.dangerFg}>You blunder. We know.</BrTag>
          <h1
            style={{
              fontSize: isMobile ? 56 : 100,
              fontWeight: 800,
              letterSpacing: isMobile ? -2 : -4,
              lineHeight: 0.92,
              margin: isMobile ? "18px 0 14px" : "24px 0 24px",
              textTransform: "uppercase",
              fontFamily: TYPE.display,
            }}
          >
            FIX THE<br />
            MISTAKE.<br />
            <span
              style={{
                background: p.accent,
                color: p.accentFg,
                padding: "0 8px",
                border: `3px solid ${p.border}`,
                boxShadow: offsetShadow(p, 5, 5),
                display: "inline-block",
                transition: "background .25s, color .25s",
              }}
            >
              WIN
            </span>{" "}
            THE<br />REMATCH.
          </h1>
          <p style={{ fontSize: isMobile ? 15 : 19, lineHeight: 1.45, maxWidth: 520, margin: 0, fontWeight: 500, color: p.fg }}>
            Connect Chess.com. Connect Lichess. We run every game through Stockfish, find the move that killed you, and serve it back until you don&rsquo;t blunder it twice.
          </p>
          <div
            style={{
              display: "flex",
              gap: 12,
              marginTop: isMobile ? 28 : 36,
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "stretch" : "center",
            }}
          >
            <BrBtn size="lg" variant="primary" onClick={onStart} full={isMobile}>Start Training →</BrBtn>
            <BrBtn size="lg" variant="ghost" withShadow={false} full={isMobile}>See an Example</BrBtn>
          </div>
          {!isMobile && (
            <div
              style={{
                marginTop: 28,
                display: "flex",
                gap: 28,
                fontSize: 12,
                fontWeight: 700,
                fontFamily: TYPE.mono,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                color: p.fgMuted,
              }}
            >
              <span>✓ No card</span>
              <span>✓ 50 games free</span>
              <span>✓ Cancel anytime</span>
            </div>
          )}
        </div>

        <div style={{ position: "relative" }}>
          <BrBox padding={isMobile ? 14 : 18} shadowSize={isMobile ? 6 : 10} label="GAME PREVIEW">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, marginTop: 4 }}>
              <div>
                <div style={{ fontSize: 10.5, fontFamily: TYPE.mono, color: p.fgMuted, textTransform: "uppercase", letterSpacing: 1 }}>
                  vs xKnight92 · Blitz 5+0
                </div>
                <div style={{ fontSize: isMobile ? 14 : 17, fontWeight: 800, marginTop: 2 }}>2 BLUNDERS FOUND</div>
              </div>
              <BrTag bg={p.danger} fg={p.dangerFg}>-3.4</BrTag>
            </div>
            <ChessBoard palette={p} size={isMobile ? 300 : 380} position={board} />
            <div
              style={{
                marginTop: 14,
                padding: 10,
                background: p.bg,
                border: `2px solid ${p.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={{ fontSize: isMobile ? 16 : 20, fontWeight: 800, fontFamily: TYPE.mono, lineHeight: 1.2 }}>
                <span style={{ color: p.danger }}>Nxe5??</span>
                <span style={{ margin: "0 6px", color: p.fgMuted }}>→</span>
                <span style={{ background: p.success, color: p.successFg, padding: "0 5px", border: `2px solid ${p.border}` }}>Nd5!</span>
              </div>
              <BrBtn size="sm" variant="accent" onClick={onStart}>Replay ↺</BrBtn>
            </div>
          </BrBox>
          {!isMobile && (
            <div
              style={{
                position: "absolute",
                top: -26,
                right: -32,
                background: p.accent,
                color: p.accentFg,
                border: `2px solid ${p.border}`,
                padding: "14px 18px",
                transform: "rotate(6deg)",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                boxShadow: offsetShadow(p, 4, 4),
                fontFamily: TYPE.display,
                fontSize: 14,
                textAlign: "center",
                lineHeight: 1.1,
              }}
            >
              +187 ELO
              <br />
              <span style={{ fontSize: 10, fontFamily: TYPE.mono }}>last 30 days</span>
            </div>
          )}
        </div>
      </section>

      <section
        style={{
          borderTop: `2px solid ${p.border}`,
          borderBottom: `2px solid ${p.border}`,
          background: p.fg,
          color: p.bg,
          padding: isMobile ? "10px 0" : "14px 0",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: isMobile ? 36 : 56,
            fontSize: isMobile ? 13 : 16,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 2,
            whiteSpace: "nowrap",
            fontFamily: TYPE.display,
            paddingLeft: 20,
          }}
        >
          {["CHESS.COM SYNC", "★", "LICHESS SYNC", "★", "PGN IMPORT", "★", "STOCKFISH 17", "★", "12,418 PLAYERS TRAINING", "★", "CHESS.COM SYNC", "★", "LICHESS SYNC", "★", "PGN IMPORT", "★"].map(
            (t, i) => (
              <span key={i}>{t}</span>
            ),
          )}
        </div>
      </section>

      <section
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          padding: isMobile ? "40px 16px" : "76px 40px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: isMobile ? 28 : 40,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 14 : 24,
            alignItems: isMobile ? "flex-start" : "flex-end",
          }}
        >
          <h2
            style={{
              fontSize: isMobile ? 44 : 64,
              fontWeight: 800,
              letterSpacing: isMobile ? -1.5 : -2.5,
              lineHeight: 0.95,
              textTransform: "uppercase",
              margin: 0,
              fontFamily: TYPE.display,
            }}
          >
            REVIEW.<br />REPLAY.<br />REPEAT.
          </h2>
          <div style={{ maxWidth: 360, fontSize: isMobile ? 14 : 16, lineHeight: 1.5, fontWeight: 500, color: p.fgMuted }}>
            Puzzle apps drill someone else&rsquo;s mistakes. We drill yours. That&rsquo;s the entire pitch.
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: isMobile ? 18 : 24,
          }}
        >
          {[
            { num: "01", t: "DEEP REVIEW", body: "Stockfish 17 scores every position. Each move tagged: tactical, positional, time. Ranked by impact on your rating.", tag: "~12 sec / game" },
            { num: "02", t: "REPLAY THE BLUNDER", body: "Mistakes become drills. Same clock pressure. Same opponent strength. Until you stop missing it.", tag: "live engine" },
            { num: "03", t: "PLAN, BY FORMAT", body: "Bullet, blitz, rapid and classical are different games. Different weakness profile for each. The plan adapts.", tag: "updates daily" },
          ].map((f) => (
            <BrBox key={f.num} padding={isMobile ? 20 : 26} shadowSize={6}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                <div style={{ fontSize: isMobile ? 44 : 56, fontWeight: 800, letterSpacing: -2, lineHeight: 1, fontFamily: TYPE.display }}>{f.num}</div>
                <BrTag>{f.tag}</BrTag>
              </div>
              <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, textTransform: "uppercase", letterSpacing: -0.4, marginBottom: 8, lineHeight: 1.1 }}>{f.t}</div>
              <p style={{ fontSize: isMobile ? 13.5 : 14, lineHeight: 1.55, margin: 0, fontWeight: 500, color: p.fgMuted }}>{f.body}</p>
            </BrBox>
          ))}
        </div>
      </section>

      <section
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          padding: isMobile ? "0 16px 40px" : "0 40px 80px",
        }}
      >
        <BrBox bg={p.fg} fg={p.bg} padding={isMobile ? 24 : 44} shadowSize={isMobile ? 6 : 10}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr",
              gap: isMobile ? 22 : 56,
              alignItems: "center",
            }}
          >
            <div>
              <h3
                style={{
                  fontSize: isMobile ? 30 : 44,
                  fontWeight: 800,
                  letterSpacing: isMobile ? -1 : -1.5,
                  margin: "0 0 14px",
                  lineHeight: 0.95,
                  textTransform: "uppercase",
                  fontFamily: TYPE.display,
                }}
              >
                4× MORE<br />BLUNDERS FIXED<br />
                <span style={{ background: p.accent, color: p.accentFg, padding: "0 8px", border: `3px solid ${p.accent}` }}>IN MONTH ONE</span>
              </h3>
              <p style={{ fontSize: isMobile ? 14 : 16, color: p.bgAlt, lineHeight: 1.5, margin: 0, fontWeight: 500 }}>
                Because you drill the pattern that loses YOU games. Not a stranger&rsquo;s 2009 puzzle book.
              </p>
              <BrBtn size="lg" variant="accent" onClick={onStart} style={{ marginTop: 22, boxShadow: offsetShadow(p, 4, 4) }} full={isMobile}>
                Start Training →
              </BrBtn>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0,
                border: `2px solid ${p.bg}`,
              }}
            >
              {[
                ["12,418", "PLAYERS"],
                ["4.2M", "GAMES"],
                ["+184", "AVG ELO / 90d"],
                ["38%", "FEWER TIME BLUNDERS"],
              ].map(([n, l], i) => (
                <div
                  key={l}
                  style={{
                    padding: isMobile ? 14 : 22,
                    borderRight: i % 2 === 0 ? `2px solid ${p.bg}` : "none",
                    borderBottom: i < 2 ? `2px solid ${p.bg}` : "none",
                  }}
                >
                  <div style={{ fontSize: isMobile ? 30 : 40, fontWeight: 800, letterSpacing: -1.5, fontFamily: TYPE.display, lineHeight: 1 }}>{n}</div>
                  <div style={{ fontSize: 10.5, color: p.bgAlt, marginTop: 4, fontFamily: TYPE.mono, letterSpacing: 1, fontWeight: 700 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        </BrBox>
      </section>

      <PricingSection onStart={onStart} onStartPro={onStartPro} />
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />

      <footer
        style={{
          borderTop: `2px solid ${p.border}`,
          padding: isMobile ? "18px 16px" : "28px 40px",
          background: p.bg,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: isMobile ? 10 : 12,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 1,
          fontFamily: TYPE.mono,
          flexWrap: "wrap",
          gap: 10,
          color: p.fgMuted,
        }}
      >
        <span>© 2026 GAMBIT LABS</span>
        {!isMobile && <span>MADE BY HUMANS WHO BLUNDERED ONCE.</span>}
        <span>PRIVACY · TERMS</span>
      </footer>
    </div>
  );
}
